import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { Crown, Pencil, Plus, Shield, Trash2, Users } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { SettingsLayout } from '../components/settings-layout'
import { MobileBottomSlots } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Dialog } from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus } from '../components/ui/mobile-action-bar'
import { api, type Role, type RolePermissions } from '../lib/api'
import { NAV_GROUPS } from '../lib/nav'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/roles',
  component: RolesPage,
})

const PERMISSION_LABELS: { key: keyof RolePermissions; label: string }[] = [
  { key: 'all', label: 'Vollzugriff (überschreibt alles)' },
  { key: 'viewTeam', label: 'Team-Daten einsehen' },
  { key: 'manageTeam', label: 'Team-Mitglieder verwalten' },
  { key: 'viewReports', label: 'Reports einsehen' },
  { key: 'manageCustomers', label: 'Kunden verwalten' },
  { key: 'manageProjects', label: 'Projekte verwalten' },
  { key: 'manageProducts', label: 'Produkte verwalten' },
  { key: 'manageInvoices', label: 'Rechnungen verwalten' },
  { key: 'manageMail', label: 'Mail verwalten' },
  { key: 'manageDocuments', label: 'Dokumente verwalten' },
  { key: 'manageFetcher', label: 'Rechnungs-Fetcher verwalten' },
  { key: 'manageTasks', label: 'Aufgaben verwalten (alle sehen/zuweisen)' },
  { key: 'manageTickets', label: 'Tickets verwalten' },
  { key: 'manageShifts', label: 'Schichtplan verwalten' },
  { key: 'manageMeetings', label: 'Meetings verwalten' },
  { key: 'manageWorkflows', label: 'Workflows verwalten' },
  { key: 'manageSettings', label: 'Einstellungen verwalten' },
  { key: 'manageLogbook', label: 'Fahrtenbücher verwalten' },
  { key: 'viewDashboardOverview', label: 'Dashboard-Übersicht sehen' },
]

function RolesPage() {
  const [dialog, setDialog] = useState<{ open: boolean; role: Role | null }>({
    open: false,
    role: null,
  })

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data, isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.listRoles(),
    enabled: meData?.user?.role === 'admin',
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  if (meData.user.role !== 'admin') {
    return (
      <SettingsLayout title="Rollen">
        <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-8 text-center">
          <p className="text-text-secondary">
            Rollenverwaltung ist nur für Administratoren sichtbar.
          </p>
        </div>
      </SettingsLayout>
    )
  }

  const roles = data?.roles ?? []

  return (
    <SettingsLayout
      title="Rollen"
      description="Benutzerdefinierte Rollen mit granularen Berechtigungen und Seitenleisten-Sichtbarkeit verwalten."
    >
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): ＋ Neue Rolle in der Bottom-Bar — derselbe
          Handler wie der Kopf-Button. */}
      <MobileBottomSlots>
        <BarAccentPlus
          ariaLabel="Neue Rolle"
          onClick={() => setDialog({ open: true, role: null })}
        />
      </MobileBottomSlots>

      {/* <640 übernimmt die Bottom-Bar — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <div className="flex phone:hidden justify-end mb-4">
        <Button onClick={() => setDialog({ open: true, role: null })}>
          <Plus className="h-4 w-4" /> Neue Rolle
        </Button>
      </div>

      {isLoading ? (
        <p className="text-text-secondary">Lade …</p>
      ) : roles.length === 0 ? (
        <p className="text-text-secondary">Keine Rollen vorhanden.</p>
      ) : (
        <ul className="overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated/40">
          {roles.map((r, i) => (
            <RoleRow
              key={r.id}
              role={r}
              isLast={i === roles.length - 1}
              onEdit={() => setDialog({ open: true, role: r })}
            />
          ))}
        </ul>
      )}

      <RoleDialog
        open={dialog.open}
        role={dialog.role}
        onClose={() => setDialog({ open: false, role: null })}
      />
    </SettingsLayout>
  )
}

function RoleRow({ role, isLast, onEdit }: { role: Role; isLast: boolean; onEdit: () => void }) {
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: () => api.deleteRole(role.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      notify.success('Rolle gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <li
      className={cn(
        // Flex statt 5-Spalten-Grid: Der Name bekommt via flex-1 allen
        // Restplatz (fixte die Früh-Truncation „Bo…" trotz Leerraum),
        // die Meta-Spalten rechts sind shrink-0.
        'group flex items-center gap-3 sm:gap-4 px-5 py-4 transition-colors hover:bg-bg-overlay/40',
        !isLast && 'border-b border-border-subtle',
      )}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent">
        {role.isAdmin ? <Crown className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-text-primary truncate">
          {role.name}
          {role.isSystem && (
            <span className="ml-2 inline-flex items-center rounded-full bg-info/15 border border-info/40 px-2 py-0.5 text-2xs text-info">
              System
            </span>
          )}
        </p>
        {role.description && (
          <p className="text-xs text-text-tertiary truncate">{role.description}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1.5 text-xs text-text-tertiary">
        <Users className="h-3.5 w-3.5" />
        <span>{role.userCount ?? 0}</span>
      </div>
      <span
        className={cn(
          'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] border',
          role.isAdmin
            ? 'bg-accent/15 border-accent/40 text-accent'
            : 'bg-info/15 border-info/40 text-info',
        )}
      >
        {role.isAdmin ? 'Admin' : 'Standard'}
      </span>
      <div className="flex shrink-0 items-center gap-1 opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onEdit}
          aria-label="Bearbeiten"
          className="text-text-tertiary hover:text-accent"
        >
          <Pencil className="h-4 w-4" />
        </Button>
        {!role.isSystem && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={async () => {
              const ok = await confirm({
                title: 'Rolle löschen',
                description: `„${role.name}“ wird unwiderruflich gelöscht.`,
                variant: 'danger',
                confirmLabel: 'Löschen',
              })
              if (ok) remove.mutate()
            }}
            aria-label="Löschen"
            className="text-text-tertiary hover:text-danger"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </li>
  )
}

function RoleDialog({
  open,
  role,
  onClose,
}: {
  open: boolean
  role: Role | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const isEdit = !!role
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [permissions, setPermissions] = useState<Record<string, boolean>>({})
  const [visibility, setVisibility] = useState<Record<string, boolean>>({})
  const [widgetVis, setWidgetVis] = useState<Record<string, boolean>>({})
  const [tab, setTab] = useState<'general' | 'permissions' | 'visibility'>('general')

  useEffect(() => {
    if (!open) return
    if (role) {
      setName(role.name)
      setDescription(role.description ?? '')
      setIsAdmin(role.isAdmin)
      setPermissions((role.permissions as Record<string, boolean>) ?? {})
      setVisibility((role.sidebarVisibility as Record<string, boolean>) ?? {})
      setWidgetVis((role.widgetVisibility as Record<string, boolean>) ?? {})
    } else {
      setName('')
      setDescription('')
      setIsAdmin(false)
      setPermissions({})
      setVisibility({})
      setWidgetVis({})
    }
    setTab('general')
  }, [open, role])

  const save = useMutation({
    mutationFn: () => {
      const data = {
        name,
        description: description || null,
        isAdmin,
        permissions: Object.keys(permissions).length > 0 ? permissions : null,
        sidebarVisibility: Object.keys(visibility).length > 0 ? visibility : null,
        widgetVisibility: Object.keys(widgetVis).length > 0 ? widgetVis : null,
      }
      if (isEdit && role) {
        return api.updateRole(role.id, data)
      }
      return api.createRole(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      // Eigene Sidebar sofort aktualisieren, falls der Admin gerade die
      // Rolle bearbeitet hat, die er selbst trägt. Andere Sessions holen
      // die Änderung über das refetchInterval auf ['auth', 'me'] (60s).
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      notify.success(isEdit ? 'Rolle aktualisiert' : 'Rolle angelegt')
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      notify.error('Name ist Pflicht')
      return
    }
    save.mutate()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? `${role?.name} bearbeiten` : 'Neue Rolle'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button onClick={onSubmit} type="button" disabled={save.isPending}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4 pb-2">
        {/* Tab bar */}
        <div className="flex gap-1 border-b border-border-subtle -mx-1 mb-1">
          {(['general', 'permissions', 'visibility'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                'px-3 py-1.5 text-xs border-b-2 transition-colors',
                tab === t
                  ? 'border-accent text-accent'
                  : 'border-transparent text-text-secondary hover:text-text-primary',
              )}
            >
              {t === 'general'
                ? 'Allgemein'
                : t === 'permissions'
                  ? 'Berechtigungen'
                  : 'Sichtbarkeit'}
            </button>
          ))}
        </div>

        {tab === 'general' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="role-name">Name</Label>
              <Input
                id="role-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                disabled={role?.isSystem && isEdit}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role-desc">Beschreibung</Label>
              <Input
                id="role-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional"
              />
            </div>
            {(!role?.isSystem || !isEdit) && (
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isAdmin}
                  onChange={(e) => setIsAdmin(e.target.checked)}
                  className="h-4 w-4 accent-accent"
                />
                <div>
                  <span className="text-sm font-medium">Admin-Rolle</span>
                  <p className="text-xs text-text-tertiary">
                    Gewährt Vollzugriff auf alle Verwaltungsfunktionen
                  </p>
                </div>
              </label>
            )}
          </div>
        )}

        {tab === 'permissions' && (
          <div className="space-y-3">
            <p className="text-xs text-text-tertiary">
              Feature-Berechtigungen steuern, welche Aktionen Benutzer mit dieser Rolle ausführen
              können. Fehlende Berechtigungen werden als nicht gewährt behandelt.
            </p>
            <div className="rounded-xl border border-border-subtle overflow-hidden max-h-72 overflow-y-auto">
              {PERMISSION_LABELS.map((p) => {
                const checked = permissions[p.key] ?? false
                return (
                  <label
                    key={p.key}
                    className="flex items-center justify-between gap-3 px-3 py-2 cursor-pointer hover:bg-bg-overlay/40 border-b border-border-subtle last:border-0"
                  >
                    <span className="text-sm">{p.label}</span>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        setPermissions({ ...permissions, [p.key]: e.target.checked })
                      }
                      className="h-4 w-4 accent-accent"
                    />
                  </label>
                )
              })}
            </div>
          </div>
        )}

        {tab === 'visibility' && (
          <div className="space-y-3">
            <p className="text-xs text-text-tertiary">
              Welche Seitenleisten-Einträge Benutzer mit dieser Rolle sehen. Fehlende Einträge
              werden als sichtbar behandelt. Benutzer-eigene Überschreibungen haben Vorrang.
            </p>
            <div className="rounded-xl border border-border-subtle overflow-hidden max-h-72 overflow-y-auto">
              {NAV_GROUPS.map((g) => (
                <div key={g.id} className="border-b border-border-subtle last:border-0">
                  {g.label && (
                    <div className="px-3 py-1 bg-bg-overlay/30">
                      <p className="text-2xs uppercase tracking-wider text-text-tertiary">
                        {g.label}
                      </p>
                    </div>
                  )}
                  {g.items.map((item) => {
                    const Icon = item.icon
                    const visible = visibility[item.key] ?? true
                    return (
                      <label
                        key={item.key}
                        className="flex items-center justify-between gap-3 px-3 py-2 cursor-pointer hover:bg-bg-overlay/40"
                      >
                        <div className="flex items-center gap-2">
                          <Icon className="h-3.5 w-3.5 text-text-tertiary" />
                          <span className="text-sm">{item.label}</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={(e) =>
                            setVisibility({ ...visibility, [item.key]: e.target.checked })
                          }
                          className="h-4 w-4 accent-accent"
                        />
                      </label>
                    )
                  })}
                </div>
              ))}
            </div>

            <p className="text-xs text-text-tertiary mt-4">
              Welche Dashboard-Widgets Benutzer mit dieser Rolle hinzufügen können.
            </p>
            <div className="rounded-xl border border-border-subtle overflow-hidden">
              <div className="px-3 py-1 bg-bg-overlay/30">
                <p className="text-2xs uppercase tracking-wider text-text-tertiary">Widgets</p>
              </div>
              {WIDGET_OPTIONS.map((w) => {
                const visible = widgetVis[w.key] ?? true
                return (
                  <label
                    key={w.key}
                    className="flex items-center justify-between gap-3 px-3 py-2 cursor-pointer hover:bg-bg-overlay/40"
                  >
                    <span className="text-sm">{w.label}</span>
                    <input
                      type="checkbox"
                      checked={visible}
                      onChange={(e) => setWidgetVis({ ...widgetVis, [w.key]: e.target.checked })}
                      className="h-4 w-4 accent-accent"
                    />
                  </label>
                )
              })}
            </div>
          </div>
        )}
      </form>
    </Dialog>
  )
}

const WIDGET_OPTIONS = [
  { key: 'my-tasks', label: 'Meine Aufgaben' },
  { key: 'weekly-soll-ist', label: 'Soll vs. Ist (Woche)' },
  { key: 'web-shortcut', label: 'Web-Verknüpfung' },
  { key: 'absence-overview', label: 'Abwesenheiten' },
  { key: 'vacation-balance', label: 'Urlaubskonto' },
  { key: 'project-budget', label: 'Abrechenbar' },
  { key: 'tax-deadlines', label: 'Steuertermine' },
  { key: 'performance-overview', label: 'Leistungsübersicht' },
  { key: 'team-status', label: 'Team-Status' },
  { key: 'range-report', label: 'Zeitraum-Report' },
]
